import { useMutation, UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { ENDPOINTS } from "@/api/application-api";
import { jsonAuthHeaders } from "@/utils/headers";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { displayErrorToast } from "@/utils/toast";
import { IS_AI_FEATURES_ENABLED } from "@/constants";
import { getRuntimeEnv, RuntimeEnvKey } from "@/utils/env";

export interface GeneratePlaygroundSessionNameRequest {
  playground_session_id: string;
  workspace_id: number;
  is_default: boolean;
}

export interface GeneratePlaygroundSessionNameResponse {
  success: boolean;
  generated_name: string;
}

const useGeneratePlaygroundSessionName = (
  userToken: string,
  options?: UseMutationOptions<
    GeneratePlaygroundSessionNameResponse,
    Error,
    GeneratePlaygroundSessionNameRequest
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    GeneratePlaygroundSessionNameResponse,
    Error,
    GeneratePlaygroundSessionNameRequest
  >(
    async ({ workspace_id, playground_session_id, is_default }) => {
      if (!IS_AI_FEATURES_ENABLED || getRuntimeEnv(RuntimeEnvKey.AuthConfig)) {
        return { success: false, generated_name: "" };
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
        displayErrorToast("Session name generation is taking too long. Please try again.");
      }, 35000);

      try {
        const response = await fetch(ENDPOINTS.generate_playground_session_name(workspace_id), {
          method: "POST",
          headers: jsonAuthHeaders(userToken),
          body: JSON.stringify({
            playground_session_id,
            is_default,
          }),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);
        return handleResponse(response);
      } catch (error) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.name === "AbortError") {
          throw new Error("Request timeout");
        }
        throw error;
      }
    },
    {
      ...options,

      onSuccess: (data, variables, context) => {
        if (!IS_AI_FEATURES_ENABLED || getRuntimeEnv(RuntimeEnvKey.AuthConfig)) {
          return;
        }
        queryClient.invalidateQueries(["playground-session"]);
        queryClient.invalidateQueries(["recentPlaygroundSessions"]);
        options?.onSuccess?.(data, variables, context);
      },
      onError: (error, ...args) => {
        displayErrorToast(`Failed to generate session name: ${error.message}`);
        options?.onError?.(error, ...args);
      },
    },
  );
};

export default useGeneratePlaygroundSessionName;
